import { H3, H4 } from "@cd2/text/build/es6"
import React, { useState } from "react"
import { PageWrapper } from "../Components/PageWrapper"
import { IRecord } from "../types/schema"
import { useAsyncInterval } from "../hooks/useInterval"
import { useGlobalState } from "../Components/GlobalStateProvider"
import { KeyValueList } from "../Components/KeyValueList"
import { ISageImportOptMap, sageImportOpts } from "../constants/sageImportOpts"
import { titleize } from "@cd2/tools/build/es6"
import { formatDate } from "@cd2/tools"
import { differenceInSeconds, isToday } from "date-fns"
import { FloatingBox } from "../Components/Misc/FloatingBox"
import { Grid } from "@cd2/components/build/es6/Grid"
import { BaseButton, List, Pagination, Spacer, TextGroupSmall } from "@cd2/components"
import { css } from "@emotion/core"
import { SageLogo } from "../stories/quoteOrders/SageLogo"
import { brandColors, colors } from "../Components/EOSThemeProvider"
import { Redirect } from "react-router-dom"
import { HoverUserLoader } from "../hoverLoaders/HoverUserLoader"
import { IBranchSettings } from "../utils/useSettings"
import { IWebClient } from "@cd2/skeem-client"

type ISageImport = IRecord<
  "sage_imports",
  "status" | "import_object" | "created_at" | "completed_at" | "user"
>
type ISageStatus =
  | "pending"
  | "in_progress"
  | "saving"
  | "saving_partial"
  | "processing"
  | "processing_now"
  | "completed"
  | "errored"

export async function getWarehouseSageIds(
  skeem: IWebClient,
  branchId?: string | null,
): Promise<number[] | undefined> {
  if (!branchId) {
    return undefined
  }
  const branch = await skeem.fetch2<IRecord<"branches"> & { settings: IBranchSettings }>({
    first: { name: "branches", attributes: ["settings"], matches: { id: branchId } },
  })
  const warehouses = branch.settings?.warehouses
  const hasWarehouses = warehouses && warehouses.length > 0
  const warehousesWithIds = await skeem.fetch2<IRecord<"warehouses", "importer_id">[]>({
    model: {
      name: "warehouses",
      attributes: ["importer_id"],
      matches: hasWarehouses ? { id: { or: warehouses.map(v => v.id) } } : { branch: branchId },
    },
  })
  // importer_id: "fc0cd68e-4690-41a2-85ea-d946ae6609ec:warehouse:13812231"
  const sageIds = warehousesWithIds
    .map(w => /warehouse:(.*)/.exec(w.importer_id)?.[1])
    .filter(Boolean)
  return sageIds.length > 0 ? sageIds.map(Number) : undefined
}

export function SageSyncPage() {
  const [sageImports, setSageImports] = useState<ISageImport[] | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [recordsPerPage] = useState(10)
  const indexOfLastRecord = currentPage * recordsPerPage
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage

  const skeem = window.skeem
  const { currentUser } = useGlobalState()
  const isActiveSync = sageImports?.some(sageImport =>
    ["pending", "in_progress", "saving", "saving_partial"].includes(sageImport.status),
  )

  useAsyncInterval(async () => {
    const imports = await skeem.fetch2<ISageImport[]>({
      model: {
        name: "sage_imports",
        attributes: ["status", "import_object", "created_at", "completed_at", "user"],
        filter: { matches: { branch: currentUser.branch.id, status: {not: "skip"} } },
        sort: { by: "created_at", direction: "desc" },
        paginate: { size: 100, page: 1 },
      },
    })
    setSageImports(imports)
  }, 1000)

  const displayDateAsJustTimeIfToday = (date: string) =>
    isToday(new Date(date)) ? formatDate(date, "H:mm") : formatDate(date, "d/M/yyyy - H:mm")

  async function initiateConnection(sageImportObject?: ISageImportOptMap) {
    const sageWarehouseIds = await getWarehouseSageIds(skeem, currentUser.branch.id)
    await skeem.mutate({
      sage_imports: {
        create: {
          status: "pending",
          import_object: {
            ...sageImportObject,
            productLocationWarehouseSageIds: sageWarehouseIds,
          },
          branch: { set: currentUser.branch.id },
          user: { set: skeem.currentSession.id },
        },
      },
    })
  }

  const btnProps = (opts: ISageImportOptMap) => ({
    onClick: () => initiateConnection(opts),
    renderLeft: () => (
      <List
        css={css`
          padding: 0 6px;
        `}
        horizontal
      >
        <div style={{ width: 40, position: "relative", height: 10 }}>
          <SageLogo color="#FFF" />
        </div>
      </List>
    ),
    theme: {
      buttonColor: colors.sage,
      padding: { topAndBottom: 12, sides: 6 },
    },
    disabled: isActiveSync,
  })

  if (currentUser && !currentUser.canRunSageImports) {
    return <Redirect to="/" />
  }

  return (
    <PageWrapper
      title="Sage sync"
      subtitle="Sync your Sage database to Fortus Live"
      breadcrumbPath={[]}
    >
      <Grid columns={[500, 500]} gap={16}>
        <div>
          <Grid gap={16}>
            <FloatingBox header={<H4>Import Sage data</H4>}>
              <List size={32}>
                <div>
                  <TextGroupSmall
                    heading="Quick sync"
                    body="Sync all stock, customer and essential Fortus Live data. This does not include pricing."
                  />
                  <Spacer />
                  <BaseButton {...btnProps(sageImportOpts.lite)}>Sync with Sage</BaseButton>
                </div>
                <div>
                  <TextGroupSmall
                    heading="Stock sync"
                    body="Sync all essential stock information. This is the quickest sync."
                  />
                  <Spacer />
                  <BaseButton {...btnProps(sageImportOpts.stockOnly)}>Sync stock data</BaseButton>
                </div>
                <div>
                  <TextGroupSmall
                    heading="Full sync"
                    body="Import all Sage information. This can take some time."
                  />
                  <Spacer />
                  <BaseButton {...btnProps(sageImportOpts.full)}>Sync all data</BaseButton>
                </div>
              </List>
            </FloatingBox>
          </Grid>
        </div>
        <List>
          <H3>Latest imports</H3>
          {sageImports
            ? sageImports.slice(indexOfFirstRecord, indexOfLastRecord).map(sageImport => (
                <FloatingBox
                  key={sageImport.id}
                  header={
                    <H4>
                      {sageImport.created_at
                        ? displayDateAsJustTimeIfToday(sageImport.created_at)
                        : ""}{" "}
                      - {sageImport.status}
                    </H4>
                  }
                  color={
                    sageImport.status === "pending"
                      ? brandColors.lightGrey
                      : sageImport.status === "error"
                      ? brandColors.redLightBackground
                      : [
                          "in_progress",
                          "saving",
                          "saving_partial",
                          "processing",
                          "processing_now",
                        ].includes(sageImport.status)
                      ? "white"
                      : brandColors.lightestMint
                  }
                >
                  <KeyValueList
                    data={[
                      {
                        label: "Importing data",
                        value: Object.keys((sageImport.import_object as any) ?? {})
                          .filter(key => (sageImport.import_object as any)[key])
                          .map(titleize)
                          .join(", "),
                      },
                      {
                        label: "Duration",
                        value: sageImport.created_at
                          ? sageImport.completed_at
                            ? `${differenceInSeconds(
                                new Date(sageImport.completed_at),
                                new Date(sageImport.created_at),
                              )} seconds`
                            : `${differenceInSeconds(
                                new Date(),
                                new Date(sageImport.created_at),
                              )} seconds (Still running)`
                          : undefined,
                      },
                      {
                        label: "User",
                        value: sageImport.user ? (
                          <HoverUserLoader id={sageImport.user.id} />
                        ) : (
                          "System"
                        ),
                      },
                    ]}
                  />
                </FloatingBox>
              ))
            : "Loading..."}
          <Pagination
            currentPage={currentPage}
            maxPages={Math.ceil((sageImports || []).length / 10)}
            gotoPage={page => setCurrentPage(page)}
          />
        </List>
      </Grid>
    </PageWrapper>
  )
}
